// Представление подписки в админке: подпись тарифа, срок и его окраска.
// Формы документов — bit-billing/app/api.py (GET /plans, GET /admin/subscriptions).
//
// Отдельного признака «платящий клиент» нет и не будет: это выводимое
// состояние. source=admin — тариф выдан руками (демо), invoice/self_serve —
// продажа. Ручная галочка рядом с этим фактом неизбежно разъедется:
// подписку продлили оплатой, а галочку переставить забыли.

export const SOURCE_LABELS = {
  admin: 'выдан админом',
  invoice: 'по счёту',
  self_serve: 'самостоятельно',
};

// Синтетический Free: клиенту без подписки bit-metrics считает по нему.
// Числа держим здесь только для подписи в карточке — источник правды в тарифе
// free из bit-billing, поэтому подставляем их лишь когда сетка не загрузилась.
export const FREE_FALLBACK = { minutes: 100, summaries: 3 };

/** Активная подписка из списка bit-billing, иначе null.
 *
 * Просрочку проверяем сами: ленивый флип active → expired в bit-billing
 * срабатывает только при чтении подписки этого клиента и свежесть не
 * гарантирует. paid_until в прошлом означает Free независимо от поля status.
 */
export function activeSubscription(items, now = new Date()) {
  const list = items || [];
  return list.find((s) => s.status === 'active' && toDate(s.paid_until) > now) || null;
}

/** email → активная подписка. Регистр не нормализован в bit_auth.users, поэтому ключ в нижнем. */
export function indexByEmail(items, now = new Date()) {
  const map = {};
  (items || []).forEach((s) => {
    if (!s.email) return;
    const key = s.email.toLowerCase();
    if (!map[key] && s.status === 'active' && toDate(s.paid_until) > now) map[key] = s;
  });
  return map;
}

/** Наивный UTC из Mongo ('2026-08-29T10:12:28') разбирается как локальное время — дописываем Z. */
export function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const hasZone = /[zZ]|[+-]\d\d:?\d\d$/.test(value);
  return new Date(hasZone ? value : `${value}Z`);
}

/** Целых дней до конца оплаченного периода; отрицательное — уже истёк. */
export function daysLeft(paidUntil, now = new Date()) {
  const until = toDate(paidUntil);
  if (!until) return null;
  return Math.ceil((until - now) / (1000 * 60 * 60 * 24));
}

/** Как показывать срок: 'expired' | 'soon' (≤7 дней) | 'ok'. */
export function expiryTone(paidUntil, now = new Date()) {
  const d = daysLeft(paidUntil, now);
  if (d === null) return 'ok';
  if (d <= 0) return 'expired';
  if (d <= 7) return 'soon';
  return 'ok';
}

/** «29 авг» — короткая дата для колонки списка. */
export function formatShortDate(value) {
  const d = toDate(value);
  if (!d) return '—';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

/** «Малый · выдан админом, до 29 авг 2026» — одна строка для карточки. */
export function describeSubscription(sub, planTitle) {
  if (!sub) return null;
  const title = planTitle || sub.plan_code;
  const source = SOURCE_LABELS[sub.source] || sub.source || '';
  const until = toDate(sub.paid_until);
  const untilText = until
    ? until.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
    : '—';
  return source ? `${title} · ${source}, до ${untilText}` : `${title}, до ${untilText}`;
}

/** Дата в формате поля <input type="date"> (UTC, чтобы не съехать через часовой пояс). */
export function toDateInput(value) {
  const d = toDate(value);
  return d ? d.toISOString().slice(0, 10) : '';
}

/** Конец суток UTC — срок «до 29.08» означает весь день 29-го, а не его полночь. */
export function endOfDayIso(dateInput) {
  return dateInput ? new Date(`${dateInput}T23:59:59Z`).toISOString() : null;
}

/** Дата через N дней в формате поля <input type="date"> — для демо-выдачи. */
export function dateInputPlusDays(days, now = new Date()) {
  const d = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
  return d.toISOString().slice(0, 10);
}
